import React, { useState, useMemo, useCallback, useEffect } from 'react';
import Layout from '../components/Layout';
import { api } from '../App';
import { Upload, Eye, Play, AlertTriangle, CheckCircle2, XCircle, FileText, Trash2, Sparkles, Loader2, X, Users, ChevronDown, ChevronUp, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const SAMPLE = `2026-02-18,VMR,ICICIBANK,buy,8150,1407.43
2026-02-18,Indira,ICICIBANK,buy,7700,1411.63
2026-02-12,VMR,TCS,sell,9032,2765.45
2026-02-12,Indira,TCS,sell,9885,2766.63
2026-02-12,Dinku,TCS,sell,5706,2766.45`;

const formatINR = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  const sign = n < 0 ? '-' : '';
  const v = Math.abs(n);
  return sign + '₹' + v.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

const parseInput = (text) => {
  const rows = [];
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  lines.forEach((line, idx) => {
    if (idx === 0 && /^(date|sl|s\.no|sno)/i.test(line)) return;
    const parts = line.split(/[\t,]/).map(p => p.trim());
    if (parts.length < 6) return;
    rows.push({
      date: parts[0], member: parts[1], symbol: parts[2],
      type: parts[3], quantity: parts[4], price: parts[5],
      note: parts[6] || '',
    });
  });
  return rows;
};

const rowsToCsv = (rows) =>
  rows.map(r => [r.date, r.member, r.symbol, r.type, r.quantity, r.price, r.note || ''].join(',')).join('\n');

const BulkImportTransactions = ({ user, setUser }) => {
  const [text, setText] = useState('');
  // Editable working set of rows (drives preview + apply once populated by AI or first Preview click)
  const [editableRows, setEditableRows] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [mode, setMode] = useState('csv'); // 'csv' | 'ai'
  const [aiText, setAiText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiNotes, setAiNotes] = useState([]);
  const [members, setMembers] = useState([]);
  const [holdingsBySymbol, setHoldingsBySymbol] = useState({}); // symbol -> [{member_id, member_name, quantity, current_price}]
  const [expandedRow, setExpandedRow] = useState(null); // idx of row whose "Split by member" panel is open
  const [splitAllocations, setSplitAllocations] = useState({}); // idx -> { member_id: qty }
  const [dupOverrides, setDupOverrides] = useState({}); // idx -> true if user wants to apply the duplicate anyway
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [aiImage, setAiImage] = useState(null); // { dataUrl, mime, previewUrl, size }

  useEffect(() => {
    api.get('/family-members')
      .then(res => setMembers(res.data || []))
      .catch(() => setMembers([]));
  }, []);

  // Auto re-validate when the user toggles any duplicate override
  useEffect(() => {
    if (Object.keys(dupOverrides).length === 0) return;
    if (!editableRows.length) return;
    runPreview(editableRows);
  }, [dupOverrides]);

  // Aggregate all lot-level holdings for a symbol into per-member totals.
  const loadHoldingsForSymbol = useCallback(async (symbol) => {
    if (!symbol) return null;
    const key = symbol.trim().toUpperCase();
    if (holdingsBySymbol[key]) return holdingsBySymbol[key];
    try {
      const res = await api.get(`/holdings/by-symbol/${encodeURIComponent(key)}`);
      const lots = res.data?.holdings || [];
      const grouped = {};
      lots.forEach(l => {
        const mid = l.member_id;
        if (!grouped[mid]) {
          grouped[mid] = {
            member_id: mid,
            member_name: l.member_name,
            quantity: 0,
            current_price: l.current_price || l.purchase_price || 0,
            pledged_qty: 0,
          };
        }
        grouped[mid].quantity += l.quantity || 0;
        if (l.pledged) grouped[mid].pledged_qty += l.quantity || 0;
      });
      const arr = Object.values(grouped).sort((a, b) => b.quantity - a.quantity);
      setHoldingsBySymbol(prev => ({ ...prev, [key]: arr }));
      return arr;
    } catch {
      setHoldingsBySymbol(prev => ({ ...prev, [key]: [] }));
      return [];
    }
  }, [holdingsBySymbol]);

  const parsedRows = useMemo(() => parseInput(text), [text]);

  const buildPayloadRows = useCallback((rows) => rows.map((r, i) => ({
    ...r,
    override_duplicate: !!dupOverrides[i],
  })), [dupOverrides]);

  const runPreview = useCallback(async (rows) => {
    if (!rows.length) {
      toast.error('No rows to validate');
      return null;
    }
    try {
      setLoading(true);
      const res = await api.post('/transactions/bulk', { rows: buildPayloadRows(rows), dry_run: true });
      setPreviewData(res.data);
      const { ok, errors, warnings } = res.data.summary;
      if (errors > 0) toast.error(`${errors} row(s) still have errors — fix inline & re-validate`);
      else if (warnings > 0) toast.warning(`${ok} row(s) ready, ${warnings} warning(s)`);
      else toast.success(`${ok} row(s) ready to apply`);
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
      return null;
    } finally {
      setLoading(false);
    }
  }, [buildPayloadRows]);

  const handleAiParse = useCallback(async () => {
    if (!aiText.trim() && !aiImage) {
      toast.error('Paste your broker update text or upload a screenshot first');
      return;
    }
    try {
      setAiLoading(true);
      setAiNotes([]);
      const payload = { text: aiText };
      if (aiImage) {
        payload.image_base64 = aiImage.dataUrl.split(',')[1];
        payload.image_mime = aiImage.mime;
      }
      const res = await api.post('/transactions/parse-natural', payload);
      const { rows, notes } = res.data;
      if (!rows.length) {
        toast.warning('AI could not find any transactions in the input');
        return;
      }
      const normalized = rows.map(r => ({
        date: r.date || '',
        member: r.member || '',
        symbol: r.symbol || '',
        type: r.type || 'buy',
        quantity: r.quantity || '',
        price: r.price || '',
        note: r.note || '',
      }));
      setEditableRows(normalized);
      setText(rowsToCsv(normalized));
      setAiNotes(notes || []);
      toast.success(`AI extracted ${rows.length} transaction(s) — edit any errors inline, then Apply`);
      await runPreview(normalized);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI parsing failed');
    } finally {
      setAiLoading(false);
    }
  }, [aiText, aiImage, runPreview]);

  const handleImageFile = useCallback((file) => {
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error(`Unsupported image type: ${file.type || 'unknown'}. Use PNG, JPEG or WebP.`);
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image too large (>8MB). Please resize before uploading.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setAiImage({
        dataUrl: e.target.result,
        mime: file.type,
        previewUrl: e.target.result,
        size: file.size,
        name: file.name || 'pasted-image',
      });
    };
    reader.readAsDataURL(file);
  }, []);

  const handleImageInput = (e) => {
    const file = e.target.files?.[0];
    handleImageFile(file);
    e.target.value = '';
  };

  const handleAiPaste = (e) => {
    const items = e.clipboardData?.items || [];
    for (const item of items) {
      if (item.type?.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        handleImageFile(file);
        return;
      }
    }
  };

  const handlePreview = useCallback(async () => {
    if (!parsedRows.length) {
      toast.error('Please paste at least one transaction row');
      return;
    }
    setEditableRows(parsedRows);
    await runPreview(parsedRows);
  }, [parsedRows, runPreview]);

  const handleApply = useCallback(async () => {
    if (!previewData || (previewData.summary?.ok || 0) === 0) {
      toast.error('No rows are ready to apply');
      return;
    }
    setConfirmOpen(true);
    // actual apply happens in confirmApply below
  }, [previewData]);

  const confirmApply = useCallback(async () => {
    setConfirmOpen(false);
    const rowsToApply = editableRows.length ? editableRows : parsedRows;

    try {
      setApplying(true);
      const res = await api.post('/transactions/bulk', { rows: buildPayloadRows(rowsToApply), dry_run: false });
      setPreviewData(res.data);
      toast.success(`Applied ${res.data.summary.ok} transaction(s) successfully`);
      setText('');
      setEditableRows([]);
      setAiText('');
      setDupOverrides({});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  }, [previewData, parsedRows, editableRows, buildPayloadRows]);

  const loadSample = () => setText(SAMPLE);
  const clearAll = () => { setText(''); setPreviewData(null); setAiText(''); setAiNotes([]); setEditableRows([]); setDupOverrides({}); setAiImage(null); };

  // Inline row editing helpers
  const updateRow = (idx, field, value) => {
    setEditableRows(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };
  const removeRow = (idx) => {
    setEditableRows(prev => prev.filter((_, i) => i !== idx));
    setPreviewData(prev => prev ? {
      ...prev,
      rows: prev.rows.filter((_, i) => i !== idx),
      summary: null, // force re-validate
    } : prev);
    toast.info('Row removed — click Re-validate to refresh totals');
  };
  const revalidate = () => runPreview(editableRows);

  const summary = previewData?.summary;
  const canApply = editableRows.length > 0 && previewData && !previewData.applied && summary && summary.ok > 0;

  const toggleSplit = async (idx, row) => {
    if (expandedRow === idx) {
      setExpandedRow(null);
      return;
    }
    setExpandedRow(idx);
    if ((row.type || '').toLowerCase() === 'sell') {
      await loadHoldingsForSymbol(row.symbol);
    }
  };

  const setAllocation = (idx, memberId, field, value) => {
    setSplitAllocations(prev => {
      const rowMap = { ...(prev[idx] || {}) };
      const cell = { ...(rowMap[memberId] || {}) };
      cell[field] = value;
      rowMap[memberId] = cell;
      return { ...prev, [idx]: rowMap };
    });
  };

  const applySplit = (idx, row) => {
    const allocs = splitAllocations[idx] || {};
    const isSell = (row.type || '').toLowerCase() === 'sell';
    const targetQty = parseFloat(row.quantity) || 0;
    const newRows = [];

    const pushRow = (memberName, alloc) => {
      const q = parseFloat(alloc?.qty || 0);
      if (q > 0) {
        const p = parseFloat(alloc?.price || row.price || 0);
        newRows.push({
          date: row.date, member: memberName, symbol: row.symbol,
          type: isSell ? 'sell' : 'buy', quantity: q, price: p,
          note: (row.note ? row.note + '; ' : '') + 'split from AI row',
        });
      }
    };

    if (isSell) {
      const holdingsList = holdingsBySymbol[(row.symbol || '').trim().toUpperCase()] || [];
      holdingsList.forEach(h => pushRow(h.member_name, allocs[h.member_id]));
    } else {
      members.forEach(m => pushRow(m.name, allocs[m.id]));
      const allocSum = newRows.reduce((s, r) => s + (parseFloat(r.quantity) || 0), 0);
      if (targetQty > 0 && allocSum < targetQty) {
        toast.error(`Allocated ${allocSum.toLocaleString('en-IN')} of ${targetQty.toLocaleString('en-IN')}. Add ${(targetQty - allocSum).toLocaleString('en-IN')} more to cover the full buy.`);
        return;
      }
    }

    if (!newRows.length) {
      toast.error(`Enter ${isSell ? 'sell' : 'buy'} quantity for at least one member`);
      return;
    }
    setEditableRows(prev => {
      const next = [...prev];
      next.splice(idx, 1, ...newRows);
      return next;
    });
    setExpandedRow(null);
    setSplitAllocations(prev => { const n = { ...prev }; delete n[idx]; return n; });
    toast.success(`Split into ${newRows.length} row(s). Click Re-validate.`);
  };

  const renderEditableRow = (row, idx) => {
    const result = previewData?.rows?.[idx];
    const status = result?.status || 'pending';
    const isSell = (row.type || '').toLowerCase() === 'sell';
    const statusColor = status === 'error' ? 'bg-red-50 text-red-700 border-red-200'
      : status === 'warning' ? 'bg-amber-50 text-amber-700 border-amber-200'
      : status === 'duplicate' ? 'bg-orange-50 text-orange-700 border-orange-200'
      : status === 'ok' ? 'bg-green-50 text-green-700 border-green-200'
      : 'bg-gray-50 text-gray-500 border-gray-200';
    const StatusIcon = status === 'error' ? XCircle
      : status === 'warning' ? AlertTriangle
      : status === 'duplicate' ? AlertTriangle
      : status === 'ok' ? CheckCircle2 : AlertTriangle;

    const symbolKey = (row.symbol || '').trim().toUpperCase();
    const holdingsList = holdingsBySymbol[symbolKey];
    const totalHeld = holdingsList ? holdingsList.reduce((s, h) => s + h.quantity, 0) : 0;
    const targetQty = parseFloat(row.quantity) || 0;
    const allocMap = splitAllocations[idx] || {};
    const allocSum = Object.values(allocMap).reduce((s, v) => s + (parseFloat(v?.qty) || 0), 0);

    return (
      <React.Fragment key={idx}>
      <tr className={`border-b ${status === 'error' ? 'bg-red-50/40' : status === 'duplicate' ? 'bg-orange-50/50' : ''}`} data-testid={`editable-row-${idx}`}>
        <td className="px-2 py-1.5 text-center align-top">
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${statusColor}`}>
            <StatusIcon size={10} /> {previewData?.applied ? '✓' : status.toUpperCase()}
          </span>
        </td>
        <td className="px-1 py-1 align-top">
          <input
            type="text"
            value={row.date || ''}
            onChange={e => updateRow(idx, 'date', e.target.value)}
            placeholder="YYYY-MM-DD"
            className="w-28 px-1.5 py-1 border border-gray-300 rounded text-xs"
            data-testid={`row-date-${idx}`}
          />
        </td>
        <td className="px-1 py-1 align-top">
          <select
            value={row.member || ''}
            onChange={e => updateRow(idx, 'member', e.target.value)}
            className="w-24 px-1 py-1 border border-gray-300 rounded text-xs bg-white"
            data-testid={`row-member-${idx}`}
          >
            <option value="">-- pick --</option>
            {members.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
          </select>
        </td>
        <td className="px-1 py-1 align-top">
          <input
            type="text"
            value={row.symbol || ''}
            onChange={e => updateRow(idx, 'symbol', e.target.value.toUpperCase())}
            className="w-28 px-1.5 py-1 border border-gray-300 rounded text-xs font-mono"
            data-testid={`row-symbol-${idx}`}
          />
        </td>
        <td className="px-1 py-1 align-top">
          <select
            value={row.type || ''}
            onChange={e => updateRow(idx, 'type', e.target.value)}
            className={`w-16 px-1 py-1 border border-gray-300 rounded text-xs font-semibold ${isSell ? 'text-red-700 bg-red-50' : 'text-green-700 bg-green-50'}`}
            data-testid={`row-type-${idx}`}
          >
            <option value="buy">buy</option>
            <option value="sell">sell</option>
          </select>
        </td>
        <td className="px-1 py-1 align-top">
          <input
            type="number"
            value={row.quantity ?? ''}
            onChange={e => updateRow(idx, 'quantity', e.target.value)}
            className="w-20 px-1.5 py-1 border border-gray-300 rounded text-xs text-right"
            data-testid={`row-qty-${idx}`}
          />
        </td>
        <td className="px-1 py-1 align-top">
          <input
            type="number"
            step="0.01"
            value={row.price ?? ''}
            onChange={e => updateRow(idx, 'price', e.target.value)}
            className={`w-24 px-1.5 py-1 border rounded text-xs text-right ${(!row.price || parseFloat(row.price) <= 0) ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
            data-testid={`row-price-${idx}`}
          />
        </td>
        <td className="px-2 py-1 text-right text-xs align-top">
          {result?.preview ? (
            isSell ? (
              <div>
                <div className={`font-medium ${result.preview.realized_pnl >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                  {formatINR(result.preview.realized_pnl)}
                </div>
                <div className="text-[10px] text-gray-500">
                  {result.preview.applied_qty}/{row.quantity}
                </div>
              </div>
            ) : (
              <div className="font-medium text-blue-700">{formatINR(result.preview.buy_value)}</div>
            )
          ) : '—'}
        </td>
        <td className="px-2 py-1 text-[11px] text-gray-600 align-top max-w-[240px]">
          {result?.messages?.length > 0 && (
            <div className={status === 'duplicate' ? 'text-orange-700' : 'text-red-600'}>{result.messages.join('; ')}</div>
          )}
          {status === 'duplicate' && (
            <label className="mt-1 flex items-center gap-1 text-[11px] font-medium text-orange-800 cursor-pointer">
              <input
                type="checkbox"
                checked={!!dupOverrides[idx]}
                onChange={e => {
                  setDupOverrides(prev => ({ ...prev, [idx]: e.target.checked }));
                }}
                className="w-3 h-3"
                data-testid={`dup-override-${idx}`}
              />
              Apply anyway (re-record this as a new lot)
            </label>
          )}
          {row.note && <div className="text-gray-500 italic">{row.note}</div>}
          {(isSell || (row.symbol && row.type)) && row.symbol && (
            <button
              onClick={() => toggleSplit(idx, row)}
              className="mt-1 text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100 flex items-center gap-1 font-medium"
              data-testid={`row-split-toggle-${idx}`}
            >
              <Users size={10} />
              {expandedRow === idx ? 'Hide members' : (isSell ? 'Show holdings & split' : 'Split across members')}
              {expandedRow === idx ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
            </button>
          )}
        </td>
        <td className="px-1 py-1 text-center align-top">
          <button
            onClick={() => removeRow(idx)}
            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
            title="Remove row"
            data-testid={`row-remove-${idx}`}
          >
            <X size={13} />
          </button>
        </td>
      </tr>
      {expandedRow === idx && (
        <tr className="bg-indigo-50/60 border-b" data-testid={`split-panel-${idx}`}>
          <td colSpan={10} className="px-4 py-3">
            {isSell ? (
              !holdingsList ? (
                <div className="text-xs text-gray-500 flex items-center gap-2">
                  <Loader2 size={12} className="animate-spin" /> Loading holdings for {row.symbol}...
                </div>
              ) : holdingsList.length === 0 ? (
                <div className="text-xs text-red-600">No current holdings found for &quot;{row.symbol}&quot; — check the symbol or add member manually.</div>
              ) : (
                <div>
                  <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
                    <div className="text-xs">
                      <span className="font-semibold text-gray-700">Current holdings of {row.symbol}:</span>
                      <span className="ml-2 text-gray-500">Total {totalHeld.toLocaleString('en-IN')} shares across {holdingsList.length} member(s)</span>
                    </div>
                    {targetQty > 0 && (
                      <div className={`text-xs font-medium ${allocSum === targetQty ? 'text-green-700' : 'text-amber-700'}`}>
                        Allocated {allocSum.toLocaleString('en-IN')} / {targetQty.toLocaleString('en-IN')}
                        {allocSum !== targetQty && ` (${targetQty - allocSum > 0 ? 'short by ' : 'over by '}${Math.abs(targetQty - allocSum).toLocaleString('en-IN')})`}
                      </div>
                    )}
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-gray-500">
                        <th className="text-left py-1 font-medium">Member</th>
                        <th className="text-right py-1 font-medium">Held Qty</th>
                        <th className="text-right py-1 font-medium">Free / Pledged</th>
                        <th className="text-right py-1 font-medium">Mkt Price</th>
                        <th className="text-right py-1 font-medium">Sell Qty</th>
                        <th className="text-right py-1 font-medium">Sell Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {holdingsList.map(h => {
                        const cell = allocMap[h.member_id] || {};
                        const alloc = cell.qty ?? '';
                        const priceAlloc = cell.price ?? '';
                        const overAlloc = parseFloat(alloc || 0) > h.quantity;
                        return (
                          <tr key={h.member_id} className="border-t border-indigo-100">
                            <td className="py-1 font-medium text-gray-800">{h.member_name}</td>
                            <td className="py-1 text-right font-mono">{h.quantity.toLocaleString('en-IN')}</td>
                            <td className="py-1 text-right text-[11px] text-gray-500">
                              {(h.quantity - h.pledged_qty).toLocaleString('en-IN')} free
                              {h.pledged_qty > 0 && <span className="text-amber-700"> · {h.pledged_qty.toLocaleString('en-IN')} pledged</span>}
                            </td>
                            <td className="py-1 text-right font-mono">₹{(h.current_price || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                            <td className="py-1 text-right">
                              <input
                                type="number"
                                min="0"
                                max={h.quantity}
                                value={alloc}
                                onChange={e => setAllocation(idx, h.member_id, 'qty', e.target.value)}
                                placeholder="0"
                                className={`w-24 px-1.5 py-1 border rounded text-xs text-right ${overAlloc ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                                data-testid={`split-alloc-${idx}-${h.member_id}`}
                              />
                            </td>
                            <td className="py-1 text-right">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={priceAlloc}
                                onChange={e => setAllocation(idx, h.member_id, 'price', e.target.value)}
                                placeholder={row.price || '0'}
                                className="w-24 px-1.5 py-1 border border-gray-300 rounded text-xs text-right"
                                data-testid={`split-price-${idx}-${h.member_id}`}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => {
                        if (!targetQty) { toast.error('Set the target Sell Qty first'); return; }
                        const total = holdingsList.reduce((s, h) => s + h.quantity, 0);
                        if (!total) return;
                        let remaining = targetQty;
                        const newAlloc = {};
                        holdingsList.forEach((h, i) => {
                          let share;
                          if (i === holdingsList.length - 1) {
                            share = Math.min(remaining, h.quantity);
                          } else {
                            share = Math.min(Math.round((h.quantity / total) * targetQty), h.quantity);
                            remaining -= share;
                          }
                          newAlloc[h.member_id] = { qty: share, price: (allocMap[h.member_id] || {}).price || row.price || '' };
                        });
                        setSplitAllocations(prev => ({ ...prev, [idx]: newAlloc }));
                      }}
                      className="text-[11px] px-2 py-1 rounded bg-white border border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                      data-testid={`split-auto-${idx}`}
                    >
                      Auto-distribute {targetQty > 0 ? `${targetQty.toLocaleString('en-IN')} shares` : ''}
                    </button>
                    <button
                      onClick={() => applySplit(idx, row)}
                      className="text-[11px] px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 font-medium"
                      data-testid={`split-apply-${idx}`}
                    >
                      Apply split → replace this row with per-member rows
                    </button>
                    <button
                      onClick={() => setExpandedRow(null)}
                      className="text-[11px] px-2 py-1 rounded bg-white border border-gray-300 text-gray-600 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )
            ) : (
              // ============= BUY SPLIT PANEL =============
              <div>
                <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
                  <div className="text-xs">
                    <span className="font-semibold text-gray-700">Split buy of {row.symbol} across members:</span>
                    <span className="ml-2 text-gray-500">Enter Buy Qty per member — the total must equal {targetQty.toLocaleString('en-IN')} shares.</span>
                  </div>
                  {targetQty > 0 && (
                    <div className={`text-xs font-semibold ${allocSum === targetQty ? 'text-green-700' : allocSum > targetQty ? 'text-red-700' : 'text-amber-700'}`}>
                      Allocated {allocSum.toLocaleString('en-IN')} / {targetQty.toLocaleString('en-IN')}
                      {allocSum < targetQty && ` — add ${(targetQty - allocSum).toLocaleString('en-IN')} more`}
                      {allocSum > targetQty && ` — remove ${(allocSum - targetQty).toLocaleString('en-IN')}`}
                    </div>
                  )}
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-500">
                      <th className="text-left py-1 font-medium">Member</th>
                      <th className="text-right py-1 font-medium">Buy Qty</th>
                      <th className="text-right py-1 font-medium">Buy Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map(m => {
                      const cell = allocMap[m.id] || {};
                      const alloc = cell.qty ?? '';
                      const priceAlloc = cell.price ?? '';
                      return (
                        <tr key={m.id} className="border-t border-indigo-100">
                          <td className="py-1 font-medium text-gray-800">
                            {m.name} <span className="text-gray-400 font-normal">({m.relation})</span>
                          </td>
                          <td className="py-1 text-right">
                            <input
                              type="number"
                              min="0"
                              value={alloc}
                              onChange={e => setAllocation(idx, m.id, 'qty', e.target.value)}
                              placeholder="0"
                              className="w-28 px-1.5 py-1 border border-gray-300 rounded text-xs text-right"
                              data-testid={`split-alloc-${idx}-${m.id}`}
                            />
                          </td>
                          <td className="py-1 text-right">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={priceAlloc}
                              onChange={e => setAllocation(idx, m.id, 'price', e.target.value)}
                              placeholder={row.price || '0'}
                              className="w-28 px-1.5 py-1 border border-gray-300 rounded text-xs text-right"
                              data-testid={`split-price-${idx}-${m.id}`}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => {
                      if (!targetQty) { toast.error('Set the target Buy Qty first'); return; }
                      if (!members.length) return;
                      const per = Math.floor(targetQty / members.length);
                      const remainder = targetQty - per * members.length;
                      const newAlloc = {};
                      members.forEach((m, i) => {
                        newAlloc[m.id] = {
                          qty: per + (i === 0 ? remainder : 0),
                          price: (allocMap[m.id] || {}).price || row.price || '',
                        };
                      });
                      setSplitAllocations(prev => ({ ...prev, [idx]: newAlloc }));
                    }}
                    className="text-[11px] px-2 py-1 rounded bg-white border border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                    data-testid={`split-auto-${idx}`}
                  >
                    Equal split {targetQty > 0 ? `${targetQty.toLocaleString('en-IN')} across ${members.length} members` : ''}
                  </button>
                  <button
                    onClick={() => applySplit(idx, row)}
                    disabled={targetQty > 0 && allocSum < targetQty}
                    className="text-[11px] px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`split-apply-${idx}`}
                    title={targetQty > 0 && allocSum < targetQty ? `Allocate all ${targetQty.toLocaleString('en-IN')} shares before applying` : ''}
                  >
                    Apply split → replace this row with per-member rows
                  </button>
                  <button
                    onClick={() => setExpandedRow(null)}
                    className="text-[11px] px-2 py-1 rounded bg-white border border-gray-300 text-gray-600 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
      </React.Fragment>
    );
  };

  return (
    <Layout user={user} setUser={setUser}>
      <div data-testid="bulk-import-page">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight mb-1">Bulk Transaction Import</h1>
          <p className="text-gray-600 text-sm">Paste CSV or plain-English broker updates. Rows appear editable below — fix any errors inline, then Apply.</p>
        </div>

        {/* Input area (full width when AI, split when CSV) */}
        <div className={`grid grid-cols-1 ${mode === 'ai' ? 'lg:grid-cols-1' : 'lg:grid-cols-2'} gap-4 mb-6`}>
          <div className="card p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5" data-testid="input-mode-tabs">
                <button
                  onClick={() => setMode('csv')}
                  className={`px-3 py-1 rounded-md text-xs font-medium flex items-center gap-1 ${mode === 'csv' ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}
                  data-testid="csv-mode-tab"
                >
                  <Upload size={12} /> CSV / Spreadsheet
                </button>
                <button
                  onClick={() => setMode('ai')}
                  className={`px-3 py-1 rounded-md text-xs font-medium flex items-center gap-1 ${mode === 'ai' ? 'bg-white shadow text-indigo-700' : 'text-gray-500'}`}
                  data-testid="ai-mode-tab"
                >
                  <Sparkles size={12} /> AI: Paste Broker Update
                </button>
              </div>
              <div className="flex gap-2">
                {mode === 'csv' && (
                  <button onClick={loadSample} className="text-xs text-indigo-600 hover:underline" data-testid="load-sample-btn">
                    Load sample
                  </button>
                )}
                <button onClick={clearAll} className="text-xs text-gray-500 hover:text-red-600 flex items-center gap-1" data-testid="clear-btn">
                  <Trash2 size={12} /> Clear
                </button>
              </div>
            </div>

            {mode === 'ai' ? (
              <>
                <textarea
                  value={aiText}
                  onChange={(e) => setAiText(e.target.value)}
                  onPaste={handleAiPaste}
                  placeholder={"Paste your broker's message OR a screenshot (Ctrl+V). Example:\n\n\"Today we bought 5000 shares of Yes Bank at 21.35 in VMR's account and sold 1200 MRPL at 152.30 from Indira's account...\""}
                  className="w-full font-mono text-sm input-field resize-y"
                  style={{ minHeight: '220px', height: '300px' }}
                  data-testid="ai-textarea"
                />

                {aiImage ? (
                  <div className="mt-2 relative rounded-lg border border-indigo-200 bg-indigo-50/50 p-2" data-testid="ai-image-preview">
                    <div className="flex items-start gap-3">
                      <img src={aiImage.previewUrl} alt="broker update" className="w-32 h-32 object-contain rounded border border-white bg-white" />
                      <div className="flex-1 text-xs text-gray-700">
                        <div className="font-medium text-indigo-800 flex items-center gap-1"><ImageIcon size={12} /> Screenshot ready</div>
                        <div className="text-gray-500 mt-0.5 truncate max-w-[240px]">{aiImage.name}</div>
                        <div className="text-gray-500">{(aiImage.size / 1024).toFixed(1)} KB · {aiImage.mime}</div>
                      </div>
                      <button
                        onClick={() => setAiImage(null)}
                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-white rounded"
                        title="Remove image"
                        data-testid="ai-image-remove"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <label className="mt-2 flex items-center gap-2 text-xs font-medium text-indigo-700 cursor-pointer hover:bg-indigo-50 px-2 py-1.5 rounded border border-dashed border-indigo-300 w-fit" data-testid="ai-image-upload-label">
                    <ImageIcon size={13} />
                    <span>Attach screenshot (PNG/JPEG/WebP)</span>
                    <span className="text-gray-400 font-normal">or paste image with Ctrl+V</span>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={handleImageInput}
                      className="hidden"
                      data-testid="ai-image-input"
                    />
                  </label>
                )}

                <div className="mt-2 text-xs text-gray-500">
                  AI extracts date, member, symbol, buy/sell, quantity &amp; price from text or screenshots. You review &amp; edit each row before anything is applied.
                </div>
                <div className="mt-3">
                  <button
                    onClick={handleAiParse}
                    disabled={aiLoading || (!aiText.trim() && !aiImage)}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="ai-parse-btn"
                  >
                    {aiLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                    {aiLoading ? 'AI is reading your update...' : (aiImage && !aiText.trim() ? 'Parse screenshot with AI' : 'Parse with AI')}
                  </button>
                </div>
              </>
            ) : (
              <>
                {aiNotes.length > 0 && (
                  <div className="mb-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 space-y-0.5" data-testid="ai-notes">
                    <p className="font-semibold flex items-center gap-1"><Sparkles size={11} /> AI notes:</p>
                    {aiNotes.map((n, i) => <p key={i}>• {n}</p>)}
                  </div>
                )}
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="2026-02-18,VMR,ICICIBANK,buy,8150,1407.43&#10;2026-02-12,Indira,TCS,sell,9885,2766.63"
                  className="w-full h-64 font-mono text-sm input-field resize-y"
                  data-testid="bulk-textarea"
                />
                <div className="mt-2 text-xs text-gray-500">
                  {parsedRows.length} row{parsedRows.length !== 1 ? 's' : ''} detected
                </div>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={handlePreview}
                    disabled={loading || !parsedRows.length}
                    className="btn-primary flex items-center gap-1 text-sm disabled:opacity-50"
                    data-testid="preview-btn"
                  >
                    <Eye size={14} /> {loading ? 'Validating...' : 'Preview'}
                  </button>
                </div>
              </>
            )}
          </div>

          {mode === 'csv' && (
            <div className="card p-4">
              {summary ? (
                <div data-testid="summary-card">
                  <h3 className="font-semibold mb-3">Validation Summary {previewData.applied && <span className="text-green-600 text-sm">(Applied ✓)</span>}</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="p-2 bg-gray-50 rounded"><p className="text-xs text-gray-500">Total rows</p><p className="text-xl font-bold">{summary.total}</p></div>
                    <div className="p-2 bg-green-50 rounded"><p className="text-xs text-gray-500">Ready to apply</p><p className="text-xl font-bold text-green-700">{summary.ok}</p></div>
                    <div className="p-2 bg-amber-50 rounded"><p className="text-xs text-gray-500">Warnings</p><p className="text-xl font-bold text-amber-700">{summary.warnings}</p></div>
                    <div className="p-2 bg-red-50 rounded"><p className="text-xs text-gray-500">Errors</p><p className="text-xl font-bold text-red-700">{summary.errors}</p></div>
                  </div>
                  {(summary.duplicates || 0) > 0 && (
                    <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-800">
                      <strong>{summary.duplicates}</strong> duplicate row{summary.duplicates !== 1 ? 's' : ''} skipped — identical transaction already exists on the same date. Remove them or edit to make them unique.
                    </div>
                  )}
                  <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                    <div className="p-2 bg-blue-50 rounded"><p className="text-xs text-gray-500">Total Buy Value</p><p className="font-semibold text-blue-700">{formatINR(summary.total_buy_value)}</p></div>
                    <div className="p-2 bg-indigo-50 rounded"><p className="text-xs text-gray-500">Total P&L (sells)</p><p className={`font-semibold ${summary.total_pnl >= 0 ? 'text-green-700' : 'text-red-600'}`}>{formatINR(summary.total_pnl)}</p></div>
                  </div>
                </div>
              ) : (
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2"><FileText size={16} /> Format Guide</h3>
                  <div className="text-sm space-y-2 text-gray-600">
                    <p><strong>Required columns (in order):</strong></p>
                    <ol className="list-decimal list-inside space-y-1 text-xs">
                      <li><strong>Date</strong> — YYYY-MM-DD or DD-MM-YYYY</li>
                      <li><strong>Member</strong> — name (e.g. VMR, Indira, Dinku)</li>
                      <li><strong>Symbol</strong> — stock symbol</li>
                      <li><strong>Type</strong> — <code className="bg-gray-100 px-1 rounded">buy</code> or <code className="bg-gray-100 px-1 rounded">sell</code></li>
                      <li><strong>Qty</strong> — positive integer</li>
                      <li><strong>Price</strong> — per-share price in ₹</li>
                      <li><strong>Note</strong> (optional)</li>
                    </ol>
                    <ul className="list-disc list-inside space-y-1 text-xs mt-2">
                      <li>Sells auto-apply via FIFO (oldest lots first)</li>
                      <li>Paste CSV/TSV directly from Excel/Google Sheets</li>
                      <li>Header row auto-detected &amp; skipped</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Editable row table (shown once we have rows from either mode) */}
        {editableRows.length > 0 && (
          <div className="card overflow-hidden" data-testid="preview-table-card">
            <div className="p-3 bg-gray-50 border-b flex items-center justify-between flex-wrap gap-2">
              <div>
                <h3 className="font-semibold">Row Preview &amp; Edit</h3>
                <p className="text-xs text-gray-500 mt-0.5">Edit any field inline. Red-highlighted cells need attention. Removed rows won&apos;t be applied.</p>
              </div>
              <div className="flex gap-2 items-center">
                {summary && (
                  <div className="text-xs text-gray-600">
                    <span className="text-green-700 font-medium">{summary.ok} ready</span>
                    {summary.errors > 0 && <span className="ml-2 text-red-700 font-medium">· {summary.errors} error(s)</span>}
                    {summary.warnings > 0 && <span className="ml-2 text-amber-700 font-medium">· {summary.warnings} warn</span>}
                    {(summary.duplicates || 0) > 0 && <span className="ml-2 text-orange-700 font-medium">· {summary.duplicates} duplicate(s) skipped</span>}
                  </div>
                )}
                <button
                  onClick={revalidate}
                  disabled={loading || !editableRows.length}
                  className="btn-primary flex items-center gap-1 text-sm disabled:opacity-50"
                  data-testid="revalidate-btn"
                >
                  <Eye size={14} /> {loading ? 'Re-validating...' : 'Re-validate'}
                </button>
                <button
                  onClick={handleApply}
                  disabled={!canApply || applying}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1 disabled:opacity-40"
                  data-testid="apply-btn"
                >
                  <Play size={14} />
                  {applying
                    ? 'Applying...'
                    : summary && summary.errors > 0
                      ? `Apply ${summary.ok} OK rows (skip ${summary.errors})`
                      : summary
                        ? `Apply ${summary.ok} row${summary.ok !== 1 ? 's' : ''}`
                        : 'Apply'}
                </button>
              </div>
            </div>
            <div className="overflow-auto" style={{ maxHeight: '65vh' }}>
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-2 py-2 text-center font-semibold w-16">Status</th>
                    <th className="px-2 py-2 text-left font-semibold">Date</th>
                    <th className="px-2 py-2 text-left font-semibold">Member</th>
                    <th className="px-2 py-2 text-left font-semibold">Symbol</th>
                    <th className="px-2 py-2 text-left font-semibold">Type</th>
                    <th className="px-2 py-2 text-right font-semibold">Qty</th>
                    <th className="px-2 py-2 text-right font-semibold">Price</th>
                    <th className="px-2 py-2 text-right font-semibold">Result</th>
                    <th className="px-2 py-2 text-left font-semibold">Notes / Errors</th>
                    <th className="px-2 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {editableRows.map(renderEditableRow)}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent data-testid="apply-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Apply transactions?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-2 text-sm text-gray-600">
                  <p>
                    <span className="font-semibold text-emerald-700">{summary?.ok || 0}</span> valid transaction{(summary?.ok || 0) !== 1 ? 's' : ''} will be applied.
                  </p>
                  {(summary?.errors || 0) > 0 && (
                    <p className="text-amber-700">
                      <span className="font-semibold">{summary.errors}</span> error row{summary.errors !== 1 ? 's' : ''} will be <span className="font-semibold">skipped</span> — you can come back later and apply them once the missing info is entered.
                    </p>
                  )}
                  {(summary?.duplicates || 0) > 0 && (
                    <p className="text-orange-700">
                      <span className="font-semibold">{summary.duplicates}</span> duplicate row{summary.duplicates !== 1 ? 's' : ''} will be skipped (tick &quot;Apply anyway&quot; on any you want to force-insert).
                    </p>
                  )}
                  <p className="text-xs text-gray-500 pt-1">This action cannot be easily undone.</p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="apply-cancel-btn">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmApply}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="apply-confirm-btn"
              >
                {(summary?.errors || 0) > 0
                  ? `Apply ${summary.ok} OK rows now`
                  : `Apply ${summary?.ok || 0} row${(summary?.ok || 0) !== 1 ? 's' : ''}`}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default BulkImportTransactions;
